export type CurrencyCode = string;

const DEFAULT_LOCALE = 'en-US';
const DISAMBIGUATED_CURRENCY_SYMBOL_BY_CODE: Partial<
  Record<CurrencyCode, string>
> = {
  HKD: 'HK$',
};

const normalizeCurrency = (currency: CurrencyCode | null | undefined) =>
  currency?.trim().toUpperCase() || 'UNKNOWN';

const parsePrice = (value: number | string) => {
  const numericPrice = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isNaN(numericPrice) ? null : numericPrice;
};

const hasFraction = (value: number) => !Number.isInteger(value);

export const formatCurrencySymbol = (
  currency: CurrencyCode | null | undefined,
) => {
  const normalizedCurrency = normalizeCurrency(currency);
  if (normalizedCurrency === 'UNKNOWN') return '';

  try {
    return (
      DISAMBIGUATED_CURRENCY_SYMBOL_BY_CODE[normalizedCurrency] ??
      new Intl.NumberFormat(DEFAULT_LOCALE, {
        style: 'currency',
        currency: normalizedCurrency,
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find(part => part.type === 'currency')?.value ??
      normalizedCurrency
    );
  } catch {
    return normalizedCurrency;
  }
};

export const formatCurrencyPrice = (
  value: number | string,
  currency: CurrencyCode | null | undefined,
) => {
  const numericPrice = parsePrice(value);
  if (numericPrice == null) return value.toString();

  const normalizedCurrency = normalizeCurrency(currency);
  if (normalizedCurrency === 'UNKNOWN') {
    return numericPrice.toLocaleString();
  }

  try {
    const formatter = new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'currency',
      currency: normalizedCurrency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: hasFraction(numericPrice) ? undefined : 0,
      maximumFractionDigits: hasFraction(numericPrice) ? undefined : 0,
    });
    const disambiguatedSymbol =
      DISAMBIGUATED_CURRENCY_SYMBOL_BY_CODE[normalizedCurrency];

    if (!disambiguatedSymbol) {
      return formatter.format(numericPrice);
    }

    return formatter
      .formatToParts(numericPrice)
      .map(part =>
        part.type === 'currency' ? disambiguatedSymbol : part.value,
      )
      .join('');
  } catch {
    return `${numericPrice.toLocaleString()} ${normalizedCurrency}`;
  }
};

export const formatCurrencyLabelForNotice = (
  currency: CurrencyCode | null | undefined,
) => normalizeCurrency(currency);
